// src/components/backoffice/auth/LoginForm.tsx
// גרסה עם DEBUG מפורט ובדיקה אוטומטית

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const LoginForm: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const { login, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();

  // DEBUG: מעקב אחרי שינויים ב-isAuthenticated
  useEffect(() => {
    console.log('📝 [DEBUG] LoginForm - isAuthenticated changed:', isAuthenticated);
    console.log('📝 [DEBUG] LoginForm - isLoading:', isLoading);
    
    // אם המשתמש כבר מחובר (יש token תקף), הפנה לדשבורד
    if (isAuthenticated && !isLoading) {
      console.log('📝 [DEBUG] LoginForm - User already authenticated! Navigating to dashboard...');
      navigate('/backoffice/', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('📝 [DEBUG] LoginForm - Submit button clicked');
    console.log('📝 [DEBUG] LoginForm - Email:', email);
    
    setError('');
    
    if (!email || !password) {
      console.log('📝 [DEBUG] LoginForm - Missing email or password');
      setError('נא למלא את כל השדות');
      return;
    }

    try {
      console.log('📝 [DEBUG] LoginForm - Calling login...');
      await login({ email, password });
      console.log('📝 [DEBUG] LoginForm - Login call completed successfully');
      
      // לא נעשה navigate כאן - נתן ל-useEffect לטפל בזה
      console.log('📝 [DEBUG] LoginForm - Waiting for useEffect to handle navigation...');
      
    } catch (error) {
      console.error('📝 [DEBUG] LoginForm - Login error:', error);
      setError(error instanceof Error ? error.message : 'שגיאה בהתחברות');
    }
  };

  // אם עדיין בוודק authentication, הצג loading
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">בודק הרשאות קיימות...</p>
        </div>
      </div>
    );
  }

  // DEBUG: הדפסת מצב נוכחי
  console.log('📝 [DEBUG] LoginForm - Current render state:', {
    isAuthenticated,
    isLoading,
    email: email || 'empty',
    hasError: !!error
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <img 
            src="/logo.svg" 
            alt="CarTax Logo" 
            className="mx-auto h-12 w-auto"
          />
          <h2 className="mt-6 text-3xl font-bold text-gray-900">
            התחברות למערכת
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Backoffice - ניהול מערכת CarTax
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                כתובת אימייל
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="admin@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
              />
            </div>
            
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                סיסמה
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="סיסמה"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
              />
            </div>
          </div>

          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="text-sm text-red-700">
                {error}
              </div>
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400"
            >
              {isLoading ? 'מתחבר...' : 'התחברות'}
            </button>
          </div>
        </form>

        {/* DEBUG INFO */}
        <div className="mt-4 p-4 bg-gray-100 rounded-md text-xs">
          <div className="font-bold mb-2">Debug Info:</div>
          <div>isAuthenticated: {isAuthenticated.toString()}</div>
          <div>isLoading: {isLoading.toString()}</div>
          <div>Email: {email || 'empty'}</div>
          <div>Error: {error || 'none'}</div>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;